import { ChevronDownIcon, ChevronRight } from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import { Button } from 'src/shared/components/ui/buttons/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/popups/dropdown-menu'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import { useSignOut } from 'src/shared/features/auth/api/use-signout'
import { ProfilePicIcon } from 'src/shared/features/home/assets/pfp'
import { useWindowSize } from 'src/shared/hooks/use-window-size'
import { cn } from 'src/shared/utils/cn'

import { useMyUserData } from '../../api/accounts/use-my-user'

import { LogoutIcon } from './icons/logout'
import AuthModal from './modals/auth'

const Container = ({
  children,
  showModal,
  setShowModal,
  initialView,
}: {
  children: React.ReactNode
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  initialView: 'login' | 'signup'
}) => {
  return (
    <>
      <div className="flex items-center gap-3">
        {children}
        <AuthModal flow={initialView} open={showModal} onClose={() => setShowModal(false)} />
      </div>
    </>
  )
}

const HeaderAuthIndicator = ({
  light = false,
  buttonClassName,
}: {
  light?: boolean
  variant?: 'marketplace' | 'home'
  buttonClassName?: string
}) => {
  const navigate = useNavigate()
  const { data: user } = useMyUserData()
  const { isSignedIn: isUserSignedIn, isConsumerSignedIn: isConsumerSignedIn } = useAuthState()
  const signOut = useSignOut({ isConsumer: true })
  const [initialView, setInitialView] = useState<'login' | 'signup'>('login')
  const [showModal, setShowModal] = useState(false)
  const { width } = useWindowSize()
  const isMobile = width < 768

  const textColorClass = light ? 'text-white' : 'text-primary'

  const handleClientAuthClick = (signup = false) => {
    if (isMobile) {
      navigate(paths.consumer.auth.login.getHref())
    } else {
      setInitialView(signup ? 'signup' : 'login')
      setShowModal(true)
    }
  }

  const handleLogout = async () => {
    await signOut.mutateAsync()
    navigate(paths.root.getHref())
  }

  const handleGoToNetwork = () => {
    navigate(paths.home.getHref())
  }

  if (isMobile) {
    if (isConsumerSignedIn) {
      return (
        <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                // onClick={() => navigate(paths.users.profile.getHref(user.username))}
                className={cn(
                  'flex min-h-[50px] w-max cursor-pointer flex-row items-center gap-0.5 py-2 text-base',
                  buttonClassName,
                )}
              >
                <ProfilePicIcon className={`size-6 ${textColorClass}`} />
                <ChevronDownIcon className={`size-4 ${textColorClass}`} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={4}
              align="end"
              className="w-[200px] flex-col items-start justify-start gap-4 rounded-[20px] border-border p-4"
            >
              <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex flex-row items-center justify-between gap-3"
                >
                  <LogoutIcon className={`ml-2 min-h-5 min-w-5 text-primary`} />
                  <p className={`text-base font-semibold text-primary`}>Log out</p>
                </button>
                <ChevronRight
                  className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Container>
      )
    }

    if (isUserSignedIn) {
      return (
        <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                // onClick={() => navigate(paths.users.profile.getHref(user.username))}
                className={cn(
                  'flex min-h-[50px] w-max cursor-pointer flex-row items-center gap-0.5 py-2 text-base',
                  buttonClassName,
                )}
              >
                {user?.profilePhoto ? (
                  <div
                    style={{
                      boxShadow: '0px 1px 6px 1px rgba(27, 48, 41, 0.10)',
                    }}
                    className="rounded-full border-2 border-white bg-white"
                  >
                    <img src={user?.profilePhoto} alt="Profile" className="size-[35px] rounded-full" />
                  </div>
                ) : (
                  <ProfilePicIcon className={`size-6 ${textColorClass}`} />
                )}
                <ChevronDownIcon className={`size-4 ${textColorClass}`} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={4}
              align="end"
              className="flex w-[220px] flex-col items-start justify-start gap-4 rounded-[20px] border-border p-4"
            >
              <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                <button
                  type="button"
                  onClick={handleGoToNetwork}
                  className="flex flex-row items-center justify-between gap-3"
                >
                  <Iconmark className={`min-h-5 min-w-5 text-primary`} />
                  <p className={`text-base font-semibold text-primary`}>Go to Network</p>
                </button>
                <ChevronRight
                  className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                />
              </DropdownMenuItem>
              <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex flex-row items-center justify-between gap-3"
                >
                  <LogoutIcon className={`min-h-5 min-w-5 text-primary`} />
                  <p className={`text-base font-semibold text-primary`}>Log out</p>
                </button>
                <ChevronRight
                  className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
                />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Container>
      )
    }

    return (
      <>
        <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
          <button
            onClick={() => handleClientAuthClick(false)}
            className={cn(
              'flex min-h-[50px] w-max cursor-pointer flex-row items-center py-2 text-base',
              buttonClassName,
            )}
          >
            <ProfilePicIcon className={`size-6 ${textColorClass}`} />
          </button>
        </Container>
      </>
    )
  }

  if (isConsumerSignedIn) {
    return (
      <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              // onClick={() => navigate(paths.users.profile.getHref(user.username))}
              className={cn(
                'flex min-h-[50px] w-max cursor-pointer flex-row items-center gap-2 rounded-full border border-[#d8d8d6] bg-[#f8f7f5] px-5 py-2 text-base',
                buttonClassName,
              )}
            >
              <ProfilePicIcon className={`size-6 ${textColorClass}`} />
              <div className="flex flex-row items-center justify-center gap-1">
                <p className={`flex size-full items-center justify-center text-base font-semibold ${textColorClass}`}>
                  Account
                </p>
                <ChevronDownIcon className={`min-h-5 min-w-5 ${textColorClass}`} />
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={4}
            align="end"
            className="w-[200px] flex-col items-start justify-start gap-4 rounded-[20px] border-border p-4"
          >
            <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
              <button type="button" onClick={handleLogout} className="flex flex-row items-center justify-between gap-3">
                <LogoutIcon className={`ml-2 min-h-5 min-w-5 text-primary`} />
                <p className={`text-base font-semibold text-primary`}>Log out</p>
              </button>
              <ChevronRight
                className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Container>
    )
  }

  if (isUserSignedIn) {
    return (
      <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              // onClick={() => navigate(paths.users.profile.getHref(user.username))}
              className={cn(
                'flex min-h-[50px] w-max cursor-pointer flex-row items-center gap-2 rounded-full border border-[#d8d8d6] bg-[#f8f7f5] px-5 py-2 text-base',
                buttonClassName,
              )}
            >
              {user?.profilePhoto ? (
                <img
                  src={user?.profilePhoto}
                  alt="Profile"
                  className="size-[35px] rounded-full border-2 border-white"
                  style={{
                    boxShadow: '0px 1px 6px 1px rgba(27, 48, 41, 0.10)',
                  }}
                />
              ) : (
                <ProfilePicIcon className={`size-6 ${textColorClass}`} />
              )}
              <div className="flex flex-row items-center justify-center gap-1">
                <p className={`flex size-full items-center justify-center text-base font-semibold ${textColorClass}`}>
                  Account
                </p>
                <ChevronDownIcon className={`min-h-5 min-w-5 ${textColorClass}`} />
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={4}
            align="end"
            className="flex w-[220px] flex-col items-start justify-start gap-4 rounded-[20px] border-border p-4"
          >
            <DropdownMenuItem className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white">
              <button
                type="button"
                onClick={handleGoToNetwork}
                className="flex flex-row items-center justify-between gap-3"
              >
                <Iconmark className={`min-h-5 min-w-5 text-primary`} />
                <p className={`text-base font-semibold text-primary`}>Go to Network</p>
              </button>
              <ChevronRight
                className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
              />
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="group flex size-full cursor-pointer items-center justify-between gap-3 p-0 text-left hover:bg-white focus:bg-white"
            >
              <button type="button" className="flex flex-row items-center justify-between gap-3">
                <LogoutIcon className={`min-h-5 min-w-5 text-primary`} />
                <p className={`text-base font-semibold text-primary`}>Log out</p>
              </button>
              <ChevronRight
                className={`min-h-6 min-w-6 text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100`}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Container>
    )
  }

  return (
    <>
      <Container showModal={showModal} setShowModal={setShowModal} initialView={initialView}>
        <Button
          onClick={() => handleClientAuthClick(false)}
          variant="default"
          className={cn(
            'w-max gap-3 rounded-full px-4 text-[13px] md:min-h-[50px] md:px-7 md:text-base',
            buttonClassName,
          )}
        >
          <ProfilePicIcon className="size-6 text-white" />
          <p className="font-semibold text-white">Sign in</p>
        </Button>
      </Container>
    </>
  )
}

export default HeaderAuthIndicator
